import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import InventoryFilters from './components/InventoryFilters';
import InventoryTable from './components/InventoryTable';
import InventoryAnalytics from './components/InventoryAnalytics';
import AddItemModal from './components/AddItemModal';
import EditItemModal from './components/EditItemModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import ImportModal from './components/ImportModal';
import { exportToExcel, formatInventoryForExport } from '../../utils/exportUtils';
import { useToast } from '../../hooks/useToast';

const InventoryManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState('inventory');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const { showToast } = useToast();

  // Mock inventory data
  const mockInventoryData = [
    {
      id: '1',
      name: 'Wireless Bluetooth Headphones',
      description: 'Premium noise-cancelling wireless headphones',
      sku: 'WBH-001-2024',
      category: 'Electronics',
      supplier: 'TechCorp Solutions',
      unitPrice: 149.99,
      currentStock: 45,
      reorderPoint: 10,
      status: 'in-stock'
    },
    {
      id: '2',
      name: 'Office Chair Ergonomic',
      description: 'Adjustable height office chair with lumbar support',
      sku: 'OCE-002-2024',
      category: 'Office Supplies',
      supplier: 'Office Pro Ltd',
      unitPrice: 299.99,
      currentStock: 8,
      reorderPoint: 15,
      status: 'low-stock'
    },
    {
      id: '3',
      name: 'Steel Raw Material Grade A',
      description: 'High-quality steel sheets for manufacturing',
      sku: 'SRM-003-2024',
      category: 'Raw Materials',
      supplier: 'Material Masters Inc',
      unitPrice: 89.50,
      currentStock: 120,
      reorderPoint: 25,
      status: 'in-stock'
    },
    {
      id: '4',
      name: 'Laptop Computer i7',
      description: 'Business laptop with Intel i7 processor',
      sku: 'LCI-004-2024',
      category: 'Electronics',
      supplier: 'TechCorp Solutions',
      unitPrice: 1299.99,
      currentStock: 0,
      reorderPoint: 5,
      status: 'out-of-stock'
    },
    {
      id: '5',
      name: 'Cardboard Packaging Boxes',
      description: 'Medium size shipping boxes',
      sku: 'CPB-005-2024',
      category: 'Packaging Materials',
      supplier: 'Eco Materials Co',
      unitPrice: 2.50,
      currentStock: 500,
      reorderPoint: 100,
      status: 'overstock'
    },
    {
      id: '6',
      name: 'Printer Paper A4',
      description: 'White copy paper 80gsm',
      sku: 'PPA-006-2024',
      category: 'Office Supplies',
      supplier: 'Office Pro Ltd',
      unitPrice: 12.99,
      currentStock: 25,
      reorderPoint: 20,
      status: 'in-stock'
    },
    {
      id: '7',
      name: 'Industrial Lubricant',
      description: 'High-performance machinery lubricant',
      sku: 'ILU-007-2024',
      category: 'Raw Materials',
      supplier: 'Material Masters Inc',
      unitPrice: 45.00,
      currentStock: 15,
      reorderPoint: 30,
      status: 'low-stock'
    },
    {
      id: '8',
      name: 'USB Flash Drive 32GB',
      description: 'Portable USB 3.0 flash drive',
      sku: 'UFD-008-2024',
      category: 'Electronics',
      supplier: 'TechCorp Solutions',
      unitPrice: 19.99,
      currentStock: 75,
      reorderPoint: 20,
      status: 'in-stock'
    }
  ];

  // Calculate real analytics data from inventory items
  const calculateAnalytics = () => {
    // Calculate total stock value
    const totalStockValue = inventoryItems.reduce((sum, item) => {
      return sum + (item.unitPrice * item.currentStock);
    }, 0);

    // Calculate total items (sum of all quantities)
    const totalItems = inventoryItems.reduce((sum, item) => sum + item.currentStock, 0);

    // Count low stock items (current stock <= reorder point)
    const lowStockItems = inventoryItems.filter(item => 
      item.currentStock <= item.reorderPoint && item.currentStock > 0
    ).length;

    // Count out of stock items
    const outOfStockItems = inventoryItems.filter(item => item.currentStock === 0).length;

    // Calculate category distribution
    const categoryStats = inventoryItems.reduce((acc, item) => {
      const cat = item.category;
      if (!acc[cat]) {
        acc[cat] = { count: 0, value: 0 };
      }
      acc[cat].count += item.currentStock;
      acc[cat].value += item.unitPrice * item.currentStock;
      return acc;
    }, {});

    // Convert to array and calculate percentages
    const totalCategoryValue = Object.values(categoryStats).reduce((sum, cat) => sum + cat.value, 0);
    const categoryData = Object.entries(categoryStats).map(([name, stats]) => ({
      name,
      value: totalCategoryValue > 0 ? ((stats.value / totalCategoryValue) * 100).toFixed(1) : 0,
      count: stats.count,
      stockValue: stats.value
    }));

    // Sort by value descending
    categoryData.sort((a, b) => parseFloat(b.value) - parseFloat(a.value));

    // Calculate average turnover rate (simplified: items sold per month)
    // In real scenario, this would come from actual sales data
    const avgTurnoverRate = (totalItems / inventoryItems.length / 30).toFixed(1);

    return {
      totalStockValue,
      totalItems,
      lowStockItems,
      outOfStockItems,
      avgTurnoverRate,
      categoryData,
      recentMovements: [
        // These would come from actual movement/transaction data in production
        {
          item: 'Recent activity',
          type: 'out',
          quantity: 0,
          timestamp: 'No recent movements'
        }
      ],
      pendingOrders: [
        // These would come from actual order data in production
        {
          supplier: 'No pending orders',
          items: 0,
          value: 0,
          status: 'N/A',
          expectedDate: 'N/A'
        }
      ]
    };
  };

  const analyticsData = calculateAnalytics();

  useEffect(() => {
    setInventoryItems(mockInventoryData);
    setFilteredItems(mockInventoryData);
  }, []);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFilterChange = (filters) => {
    let filtered = inventoryItems;

    // Apply search filter
    if (filters?.search) {
      filtered = filtered?.filter(item =>
        item?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        item?.sku?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    // Apply category filter (exact match)
    if (filters?.category) {
      filtered = filtered?.filter(item => item?.category === filters?.category);
    }

    // Apply supplier filter (exact match)
    if (filters?.supplier) {
      filtered = filtered?.filter(item => item?.supplier === filters?.supplier);
    }

    // Apply status filter (exact match)
    if (filters?.status) {
      filtered = filtered?.filter(item => item?.status === filters?.status);
    }

    setFilteredItems(filtered);
  };

  const handleBulkAction = (action, itemIds) => {
    console.log(`Performing ${action} on items:`, itemIds);
    // Implement bulk action logic here
    setSelectedItems([]);
  };

  const handleQuantityUpdate = (itemId, newQuantity) => {
    const updatedItems = inventoryItems?.map(item => {
      if (item?.id === itemId) {
        const updatedItem = { ...item, currentStock: newQuantity };
        // Update status based on new quantity
        if (newQuantity === 0) {
          updatedItem.status = 'out-of-stock';
        } else if (newQuantity <= item?.reorderPoint) {
          updatedItem.status = 'low-stock';
        } else if (newQuantity > item?.reorderPoint * 3) {
          updatedItem.status = 'overstock';
        } else {
          updatedItem.status = 'in-stock';
        }
        return updatedItem;
      }
      return item;
    });

    setInventoryItems(updatedItems);
    setFilteredItems(updatedItems);
  };

  const handleItemEdit = (itemId) => {
    const item = inventoryItems.find(i => i.id === itemId);
    if (item) {
      setEditingItem(item);
      setIsEditModalOpen(true);
    }
  };

  const handleUpdateItem = (updatedItem) => {
    const updatedItems = inventoryItems.map(item => 
      item.id === updatedItem.id ? updatedItem : item
    );
    setInventoryItems(updatedItems);
    setFilteredItems(updatedItems);
  };

  const handleItemDelete = (itemId) => {
    const item = inventoryItems.find(i => i.id === itemId);
    if (item) {
      setDeletingItem(item);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;

    setIsDeleting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      const updatedItems = inventoryItems.filter(item => item.id !== deletingItem.id);
      setInventoryItems(updatedItems);
      setFilteredItems(updatedItems);
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddItem = (newItem) => {
    const updatedItems = [...inventoryItems, newItem];
    setInventoryItems(updatedItems);
    setFilteredItems(updatedItems);
  };

  const handleExportData = () => {
    try {
      // Use filtered items if there's an active filter, otherwise use all items
      const itemsToExport = filteredItems.length > 0 ? filteredItems : inventoryItems;
      
      if (itemsToExport.length === 0) {
        showToast('No data to export', 'warning');
        return;
      }

      // Format the data for export
      const formattedData = formatInventoryForExport(itemsToExport);

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `Inventory_Export_${date}`;

      // Export to Excel
      const result = exportToExcel(formattedData, filename, 'Inventory Items');

      if (result.success) {
        showToast(`Successfully exported ${itemsToExport.length} items to Excel`, 'success');
      } else {
        showToast('Failed to export data', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showToast('An error occurred while exporting', 'error');
    }
  };

  const handleImportData = () => {
    setIsImportModalOpen(true);
  };

  const handleImportItems = (importedItems) => {
    try {
      // Check for duplicate SKUs
      const existingSKUs = new Set(inventoryItems.map(item => item.sku));
      const duplicates = importedItems.filter(item => existingSKUs.has(item.sku));
      
      if (duplicates.length > 0) {
        showToast(
          `Warning: ${duplicates.length} item(s) with duplicate SKUs were skipped`,
          'warning'
        );
      }

      // Filter out duplicates and add new items
      const newItems = importedItems.filter(item => !existingSKUs.has(item.sku));
      const updatedItems = [...inventoryItems, ...newItems];
      
      setInventoryItems(updatedItems);
      setFilteredItems(updatedItems);
      
      showToast(
        `Successfully imported ${newItems.length} new item(s)`,
        'success'
      );
    } catch (error) {
      console.error('Import error:', error);
      showToast('An error occurred while importing items', 'error');
    }
  };

  const tabs = [
    { id: 'inventory', label: 'Inventory Items', icon: 'Package' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={handleSidebarToggle} 
      />
      <div className={`transition-smooth ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header sidebarCollapsed={sidebarCollapsed} />
        
        <main className="pt-16 p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Inventory Management</h1>
                <p className="text-muted-foreground mt-1">
                  Track and manage your business inventory and stock levels
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleImportData}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Import
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportData}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button>
                <Button
                  onClick={() => setIsAddModalOpen(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Item
                </Button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'inventory' && (
              <div className="space-y-6">
                <InventoryFilters
                  onFilterChange={handleFilterChange}
                  onBulkAction={handleBulkAction}
                  selectedItems={selectedItems}
                />
                
                <InventoryTable
                  items={filteredItems}
                  onItemSelect={setSelectedItems}
                  selectedItems={selectedItems}
                  onQuantityUpdate={handleQuantityUpdate}
                  onItemEdit={handleItemEdit}
                  onItemDelete={handleItemDelete}
                />
              </div>
            )}

            {activeTab === 'analytics' && (
              <InventoryAnalytics analyticsData={analyticsData} />
            )}
          </div>
        </main>
      </div>
      <AddItemModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddItem={handleAddItem}
      />
      <EditItemModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingItem(null);
        }}
        onUpdateItem={handleUpdateItem}
        item={editingItem}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={handleConfirmDelete}
        item={deletingItem}
        isDeleting={isDeleting}
      />
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportItems}
      />
    </div>
  );
};

export default InventoryManagement;